import { writable } from 'svelte/store';
import { APP_SETTING_SCOPES, loadAppSetting, resetAppSetting, saveAppSetting } from '$lib/app-settings.js';

export const MESSENGER_LAYOUT_SETTINGS_KEY = 'messenger:layout-settings';
export const MESSENGER_LAYOUT_APP_SETTING_KEY = 'messenger.layout';
export const MESSENGER_LAYOUT_CHANGED_EVENT = 'messenger-layout-settings-changed';

export const MESSENGER_CONTENT_BACKGROUNDS = {
  calm: {
    label: 'Calm',
    background:
      'radial-gradient(circle at 18% 0%, rgba(59, 130, 246, 0.08), transparent 26rem), radial-gradient(circle at 82% 100%, rgba(148, 163, 184, 0.08), transparent 24rem), linear-gradient(180deg, #f8fafc 0%, #f4f7fb 54%, #eef2f7 100%)',
    size: 'cover',
    position: 'center',
    repeat: 'no-repeat'
  },
  aurora: {
    label: 'Aurora',
    background:
      'radial-gradient(circle at 10% 20%, rgba(34, 211, 238, 0.24), transparent 22rem), radial-gradient(circle at 90% 12%, rgba(96, 165, 250, 0.18), transparent 24rem), linear-gradient(135deg, #f8fafc 0%, #eef6ff 52%, #f8fbff 100%)',
    size: 'cover',
    position: 'center',
    repeat: 'no-repeat'
  },
  paper: {
    label: 'Paper',
    background:
      'linear-gradient(135deg, rgba(148, 163, 184, 0.12) 1px, transparent 1px), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    size: '18px 18px, cover',
    position: 'center',
    repeat: 'repeat, no-repeat'
  },
  warm: {
    label: 'Warm',
    background:
      'radial-gradient(circle at 88% 84%, rgba(251, 146, 60, 0.18), transparent 23rem), radial-gradient(circle at 12% 14%, rgba(253, 186, 116, 0.12), transparent 18rem), linear-gradient(180deg, #fffdf8 0%, #fff7ed 100%)',
    size: 'cover',
    position: 'center',
    repeat: 'no-repeat'
  },
  mint: {
    label: 'Mint',
    background:
      'radial-gradient(circle at 14% 18%, rgba(45, 212, 191, 0.18), transparent 22rem), radial-gradient(circle at 86% 86%, rgba(16, 185, 129, 0.12), transparent 24rem), linear-gradient(180deg, #f8fffb 0%, #ecfdf5 100%)',
    size: 'cover',
    position: 'center',
    repeat: 'no-repeat'
  }
};

export const MESSENGER_SPLIT_THEMES = {
  clean: {
    label: 'Clean',
    description: 'Mỏng, ít nhiễu, phù hợp vận hành mặc định.',
    gapColor: '#f8fafc',
    dividerColor: '#cbd5e1',
    dividerHoverColor: '#2563eb',
    paneBorderColor: '#e2e8f0',
    paneBorderWidth: 1,
    dividerSize: 2,
    splitterHitSize: 8
  },
  soft: {
    label: 'Soft',
    description: 'Nền gap dịu, viền rõ vừa đủ.',
    gapColor: '#eef6ff',
    dividerColor: '#93c5fd',
    dividerHoverColor: '#1d4ed8',
    paneBorderColor: '#bfdbfe',
    paneBorderWidth: 1,
    dividerSize: 3,
    splitterHitSize: 10
  },
  glass: {
    label: 'Glass',
    description: 'Sáng, nhẹ, hợp nền messenger hiện tại.',
    gapColor: '#ffffff',
    dividerColor: '#dbeafe',
    dividerHoverColor: '#38bdf8',
    paneBorderColor: '#e0f2fe',
    paneBorderWidth: 1,
    dividerSize: 2,
    splitterHitSize: 8
  },
  graphite: {
    label: 'Graphite',
    description: 'Tương phản cao để kiểm tra layout.',
    gapColor: '#111827',
    dividerColor: '#64748b',
    dividerHoverColor: '#f97316',
    paneBorderColor: '#475569',
    paneBorderWidth: 1,
    dividerSize: 2,
    splitterHitSize: 10
  }
};

export const MESSENGER_LAYOUT_PRESETS = {
  balanced: {
    label: 'Desktop',
    target: 'desktop',
    description: 'Bố cục desktop cân bằng.',
    leftSize: 27,
    leftMin: 24,
    leftMax: 34,
    rightSize: 24,
    rightMin: 18,
    rightMax: 32,
    commentCollapsedSize: 13,
    commentExpandedSize: 17,
    mainSizes: [{ size: 7 }, { size: 93 }, { size: 0 }],
    contentHeadSize: 5
  },
  focus: {
    label: 'Focus',
    target: 'desktop',
    description: 'Ưu tiên nội dung tin nhắn.',
    leftSize: 23,
    leftMin: 20,
    leftMax: 30,
    rightSize: 20,
    rightMin: 16,
    rightMax: 28,
    commentCollapsedSize: 10,
    commentExpandedSize: 15,
    mainSizes: [{ size: 6 }, { size: 94 }, { size: 0 }],
    contentHeadSize: 4
  },
  overview: {
    label: 'Wide',
    target: 'desktop',
    description: 'Màn hình rộng, panel phụ rõ hơn.',
    leftSize: 31,
    leftMin: 26,
    leftMax: 38,
    rightSize: 27,
    rightMin: 20,
    rightMax: 34,
    commentCollapsedSize: 13,
    commentExpandedSize: 18,
    mainSizes: [{ size: 8 }, { size: 92 }, { size: 0 }],
    contentHeadSize: 5
  },
  ipad: {
    label: 'iPad',
    target: 'tablet',
    description: 'Tablet/iPad ngang, ưu tiên chat.',
    leftSize: 26,
    leftMin: 22,
    leftMax: 32,
    rightSize: 0,
    rightMin: 0,
    rightMax: 24,
    commentCollapsedSize: 10,
    commentExpandedSize: 14,
    mainSizes: [{ size: 6 }, { size: 94 }, { size: 0 }],
    contentHeadSize: 4
  },
  compact: {
    label: 'Tablet',
    target: 'tablet',
    description: 'Tablet nhỏ hoặc laptop hẹp.',
    leftSize: 24,
    leftMin: 20,
    leftMax: 30,
    rightSize: 21,
    rightMin: 0,
    rightMax: 26,
    commentCollapsedSize: 9,
    commentExpandedSize: 14,
    mainSizes: [{ size: 6 }, { size: 94 }, { size: 0 }],
    contentHeadSize: 4
  },
  mobileLandscape: {
    label: 'Mobile ngang',
    target: 'mobile',
    description: 'Mobile ngang, giảm panel phụ.',
    leftSize: 20,
    leftMin: 16,
    leftMax: 28,
    rightSize: 0,
    rightMin: 0,
    rightMax: 18,
    commentCollapsedSize: 8,
    commentExpandedSize: 12,
    mainSizes: [{ size: 5 }, { size: 95 }, { size: 0 }],
    contentHeadSize: 3
  }
};

const DEFAULT_SETTINGS = {
  uiLayout: 'balanced',
  splitTheme: 'clean',
  resizeEnabled: false,
  showResizeGrip: false,
  gripPosition: 'center',
  gapColor: '#f8fafc',
  dividerColor: '#cbd5e1',
  dividerHoverColor: '#2563eb',
  dividerSize: 2,
  splitterHitSize: 8,
  paneBorderEnabled: true,
  paneBorderColor: '#cbd5e1',
  paneBorderWidth: 1,
  contentBackgroundMode: 'default',
  contentBackgroundPreset: 'calm',
  contentBackgroundImage: '',
  contentBackgroundFit: 'cover',
  resizeCursor: 'auto',
  persistResizableLayout: true,
  layoutVersion: 0
};

const GRIP_POSITIONS = new Set(['start', 'center', 'end', 'full']);
const RESIZE_CURSORS = new Set(['auto', 'grab', 'col-resize', 'row-resize', 'ew-resize', 'ns-resize']);
const SPLIT_THEMES = new Set(Object.keys(MESSENGER_SPLIT_THEMES));
const CONTENT_BACKGROUND_MODES = new Set(['default', 'preset', 'custom']);
const CONTENT_BACKGROUND_PRESETS = new Set(Object.keys(MESSENGER_CONTENT_BACKGROUNDS));
const CONTENT_BACKGROUND_FITS = new Set(['cover', 'contain', 'repeat']);
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const DATA_IMAGE_PATTERN = /^data:image\/(png|jpe?g|webp|gif);base64,[a-z0-9+/=]+$/i;

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeHexColor(value, fallback) {
  return HEX_COLOR_PATTERN.test(String(value || '')) ? String(value) : fallback;
}

function normalizeDividerSize(value) {
  const size = Number(value);
  if (!Number.isFinite(size)) return DEFAULT_SETTINGS.dividerSize;
  return Math.min(12, Math.max(1, Math.round(size)));
}

function normalizeSplitterHitSize(value) {
  const size = Number(value);
  if (!Number.isFinite(size)) return DEFAULT_SETTINGS.splitterHitSize;
  return Math.min(20, Math.max(1, Math.round(size)));
}

function normalizePaneBorderWidth(value) {
  const size = Number(value);
  if (!Number.isFinite(size)) return DEFAULT_SETTINGS.paneBorderWidth;
  return Math.min(8, Math.max(0, Math.round(size)));
}

function normalizeContentBackgroundImage(value) {
  const image = String(value || '');
  if (!image) return '';
  return DATA_IMAGE_PATTERN.test(image) ? image : '';
}

function buildContentBackground(settings) {
  if (settings.contentBackgroundMode === 'preset') {
    const preset = MESSENGER_CONTENT_BACKGROUNDS[settings.contentBackgroundPreset];
    if (preset) {
      return {
        background: preset.background,
        size: preset.size,
        position: preset.position,
        repeat: preset.repeat
      };
    }
  }

  if (settings.contentBackgroundMode === 'custom' && settings.contentBackgroundImage) {
    const fit = settings.contentBackgroundFit;
    return {
      background: `linear-gradient(rgba(248, 250, 252, 0.18), rgba(248, 250, 252, 0.18)), url("${settings.contentBackgroundImage}")`,
      size: fit === 'repeat' ? 'auto' : fit,
      position: 'center',
      repeat: fit === 'repeat' ? 'repeat' : 'no-repeat'
    };
  }

  return {
    background: MESSENGER_CONTENT_BACKGROUNDS.calm.background,
    size: MESSENGER_CONTENT_BACKGROUNDS.calm.size,
    position: MESSENGER_CONTENT_BACKGROUNDS.calm.position,
    repeat: MESSENGER_CONTENT_BACKGROUNDS.calm.repeat
  };
}

export function normalizeMessengerLayoutSettings(value = {}) {
  const source = value && typeof value === 'object' ? value : {};
  const uiLayout = Object.prototype.hasOwnProperty.call(MESSENGER_LAYOUT_PRESETS, source.uiLayout)
    ? source.uiLayout
    : DEFAULT_SETTINGS.uiLayout;
  const splitTheme = SPLIT_THEMES.has(source.splitTheme) ? source.splitTheme : DEFAULT_SETTINGS.splitTheme;
  const gripPosition = GRIP_POSITIONS.has(source.gripPosition) ? source.gripPosition : DEFAULT_SETTINGS.gripPosition;
  const resizeCursor = RESIZE_CURSORS.has(source.resizeCursor) ? source.resizeCursor : DEFAULT_SETTINGS.resizeCursor;
  const contentBackgroundMode = CONTENT_BACKGROUND_MODES.has(source.contentBackgroundMode)
    ? source.contentBackgroundMode
    : DEFAULT_SETTINGS.contentBackgroundMode;
  const contentBackgroundPreset = CONTENT_BACKGROUND_PRESETS.has(source.contentBackgroundPreset)
    ? source.contentBackgroundPreset
    : DEFAULT_SETTINGS.contentBackgroundPreset;
  const contentBackgroundFit = CONTENT_BACKGROUND_FITS.has(source.contentBackgroundFit)
    ? source.contentBackgroundFit
    : DEFAULT_SETTINGS.contentBackgroundFit;
  const resizeEnabled = Boolean(source.resizeEnabled ?? DEFAULT_SETTINGS.resizeEnabled);
  const dividerSize = normalizeDividerSize(source.dividerSize);
  const splitterHitSize = normalizeSplitterHitSize(source.splitterHitSize);

  return {
    ...DEFAULT_SETTINGS,
    ...source,
    uiLayout,
    splitTheme,
    gripPosition,
    gapColor: normalizeHexColor(source.gapColor, DEFAULT_SETTINGS.gapColor),
    dividerColor: normalizeHexColor(source.dividerColor, DEFAULT_SETTINGS.dividerColor),
    dividerHoverColor: normalizeHexColor(source.dividerHoverColor, DEFAULT_SETTINGS.dividerHoverColor),
    dividerSize,
    splitterHitSize,
    paneBorderEnabled: Boolean(source.paneBorderEnabled ?? DEFAULT_SETTINGS.paneBorderEnabled),
    paneBorderColor: normalizeHexColor(source.paneBorderColor || source.dividerColor, DEFAULT_SETTINGS.paneBorderColor),
    paneBorderWidth: normalizePaneBorderWidth(source.paneBorderWidth),
    contentBackgroundMode,
    contentBackgroundPreset,
    contentBackgroundImage: normalizeContentBackgroundImage(source.contentBackgroundImage),
    contentBackgroundFit,
    resizeCursor,
    resizeEnabled,
    showResizeGrip: resizeEnabled && Boolean(source.showResizeGrip ?? source.resizeEnabled ?? DEFAULT_SETTINGS.showResizeGrip),
    persistResizableLayout: Boolean(source.persistResizableLayout ?? DEFAULT_SETTINGS.persistResizableLayout),
    layoutVersion: Number.isFinite(source.layoutVersion) ? source.layoutVersion : DEFAULT_SETTINGS.layoutVersion
  };
}

export function getMessengerLayoutPreset(uiLayout) {
  return MESSENGER_LAYOUT_PRESETS[uiLayout] || MESSENGER_LAYOUT_PRESETS[DEFAULT_SETTINGS.uiLayout];
}

function readSettings() {
  if (typeof window === 'undefined') return normalizeMessengerLayoutSettings();

  try {
    const saved = window.localStorage.getItem(MESSENGER_LAYOUT_SETTINGS_KEY);
    return normalizeMessengerLayoutSettings(saved ? JSON.parse(saved) : DEFAULT_SETTINGS);
  } catch (error) {
    console.warn('Invalid messenger layout settings:', error);
    return normalizeMessengerLayoutSettings();
  }
}

function writeSettings(settings) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(MESSENGER_LAYOUT_SETTINGS_KEY, JSON.stringify(settings));
    window.dispatchEvent(new CustomEvent(MESSENGER_LAYOUT_CHANGED_EVENT, { detail: settings }));
  } catch (error) {
    console.warn('Cannot save messenger layout settings:', error);
  }
}

export const messengerLayoutSettingsStatus = writable({
  loading: false,
  saving: false,
  error: '',
  source: 'cache',
  canManageSystem: false,
  savedAt: '',
  loadedAt: '',
  clearedUserOverrides: 0,
  system: null,
  user: null
});

function getSettingSnapshot(settings) {
  return JSON.stringify(normalizeMessengerLayoutSettings(settings));
}

function getErrorMessage(error, fallback) {
  return error?.message || fallback;
}

function createMessengerLayoutSettingsStore() {
  const store = writable(readSettings());
  let ready = false;
  let applyingRemote = false;
  let serverLoaded = false;
  let touchedBeforeServerLoad = false;
  let saveTimer;
  let saveSequence = 0;
  const savedSnapshots = {
    [APP_SETTING_SCOPES.USER]: '',
    [APP_SETTING_SCOPES.SYSTEM]: ''
  };

  function updateStatus(patch) {
    messengerLayoutSettingsStatus.update((current) => ({
      ...current,
      ...patch
    }));
  }

  function getCurrentSettings() {
    let currentSettings = normalizeMessengerLayoutSettings();
    const unsubscribe = store.subscribe((value) => {
      currentSettings = normalizeMessengerLayoutSettings(value);
    });
    unsubscribe();
    return currentSettings;
  }

  function rememberServerResponse(response) {
    const system = response?.system ? normalizeMessengerLayoutSettings(response.system) : null;
    const user = response?.user ? normalizeMessengerLayoutSettings(response.user) : null;
    const effective = normalizeMessengerLayoutSettings(response?.effective);

    savedSnapshots[APP_SETTING_SCOPES.SYSTEM] = system ? getSettingSnapshot(system) : '';
    savedSnapshots[APP_SETTING_SCOPES.USER] = user ? getSettingSnapshot(user) : '';

    updateStatus({
      loading: false,
      error: '',
      source: response?.source || 'default',
      canManageSystem: Boolean(response?.canManageSystem),
      loadedAt: new Date().toISOString(),
      system,
      user
    });

    return effective;
  }

  async function loadServerSettings({ apply = true } = {}) {
    if (typeof window === 'undefined') return { effective: normalizeMessengerLayoutSettings() };

    updateStatus({ loading: true, error: '' });

    try {
      const response = await loadAppSetting(MESSENGER_LAYOUT_APP_SETTING_KEY);
      const effective = rememberServerResponse(response);
      serverLoaded = true;

      if (apply && !touchedBeforeServerLoad) {
        applyingRemote = true;
        store.set(effective);
        applyingRemote = false;
      }

      return response;
    } catch (error) {
      updateStatus({
        loading: false,
        error: getErrorMessage(error, 'Không thể tải Layout Messenger từ server.')
      });
      throw error;
    }
  }

  async function flushServerSave(settings, scope = APP_SETTING_SCOPES.USER, options = {}) {
    if (typeof window === 'undefined') return normalizeMessengerLayoutSettings(settings);

    const normalizedScope = scope === APP_SETTING_SCOPES.SYSTEM ? APP_SETTING_SCOPES.SYSTEM : APP_SETTING_SCOPES.USER;
    const applyToAllUsers = normalizedScope === APP_SETTING_SCOPES.SYSTEM && options.applyToAllUsers === true;
    const normalized = normalizeMessengerLayoutSettings(settings);
    const snapshot = getSettingSnapshot(normalized);

    if (!applyToAllUsers && snapshot === savedSnapshots[normalizedScope]) {
      updateStatus({ saving: false, error: '' });
      return { success: true, skipped: true, clearedUserOverrides: 0 };
    }

    const sequence = ++saveSequence;
    updateStatus({ saving: true, error: '' });

    try {
      const response = await saveAppSetting(MESSENGER_LAYOUT_APP_SETTING_KEY, normalized, normalizedScope, {
        applyToAllUsers
      });
      savedSnapshots[normalizedScope] = snapshot;
      if (applyToAllUsers) {
        savedSnapshots[APP_SETTING_SCOPES.USER] = '';
      }
      if (sequence === saveSequence) {
        updateStatus({
          saving: false,
          source: normalizedScope,
          savedAt: new Date().toISOString(),
          clearedUserOverrides: response?.clearedUserOverrides || 0,
          [normalizedScope]: normalized,
          ...(applyToAllUsers ? { user: null } : {})
        });
      }
      return response;
    } catch (error) {
      if (sequence === saveSequence) {
        updateStatus({
          saving: false,
          error: getErrorMessage(error, 'Không thể lưu Layout Messenger.')
        });
      }
      throw error;
    }
  }

  function scheduleServerSave(settings) {
    if (typeof window === 'undefined') return;

    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      flushServerSave(settings, APP_SETTING_SCOPES.USER).catch(() => {});
    }, 450);
  }

  store.subscribe((value) => {
    const normalized = normalizeMessengerLayoutSettings(value);
    if (!ready) {
      ready = true;
      return;
    }
    writeSettings(normalized);

    if (applyingRemote) return;
    if (!serverLoaded) touchedBeforeServerLoad = true;
    scheduleServerSave(normalized);
  });

  function setSettings(nextSettings) {
    store.set(normalizeMessengerLayoutSettings(nextSettings));
  }

  function updateSettings(updater) {
    store.update((current) => normalizeMessengerLayoutSettings(updater(normalizeMessengerLayoutSettings(current))));
  }

  function resetSettings() {
    setSettings(DEFAULT_SETTINGS);
  }

  async function saveScope(scope = APP_SETTING_SCOPES.USER) {
    clearTimeout(saveTimer);
    return flushServerSave(getCurrentSettings(), scope);
  }

  async function applySystemToAllUsers() {
    clearTimeout(saveTimer);
    return flushServerSave(getCurrentSettings(), APP_SETTING_SCOPES.SYSTEM, { applyToAllUsers: true });
  }

  async function resetScope(scope = APP_SETTING_SCOPES.USER) {
    clearTimeout(saveTimer);
    const normalizedScope = scope === APP_SETTING_SCOPES.SYSTEM ? APP_SETTING_SCOPES.SYSTEM : APP_SETTING_SCOPES.USER;
    updateStatus({ loading: true, error: '' });

    try {
      await resetAppSetting(MESSENGER_LAYOUT_APP_SETTING_KEY, normalizedScope);
      savedSnapshots[normalizedScope] = '';
      touchedBeforeServerLoad = false;
      return loadServerSettings({ apply: true });
    } catch (error) {
      updateStatus({
        loading: false,
        error: getErrorMessage(error, 'Không thể reset Layout Messenger.')
      });
      throw error;
    }
  }

  if (typeof window !== 'undefined') {
    queueMicrotask(() => {
      loadServerSettings({ apply: true }).catch(() => {});
    });
  }

  return {
    subscribe: store.subscribe,
    set: setSettings,
    update: updateSettings,
    reset: resetSettings,
    save: saveScope,
    applySystemToAllUsers,
    reload: loadServerSettings,
    resetScope
  };
}

export const messengerLayoutSettings = createMessengerLayoutSettingsStore();

export function buildMessengerLayoutOption(baseOption = {}, settings = normalizeMessengerLayoutSettings()) {
  const normalized = normalizeMessengerLayoutSettings(settings);
  const preset = getMessengerLayoutPreset(normalized.uiLayout);
  const commentSize = baseOption.commentSize ?? (baseOption.replyActive ? preset.commentExpandedSize : preset.commentCollapsedSize);
  const headSize = baseOption.contentHeadSize ?? preset.contentHeadSize;
  const normalizedCommentSize = Math.max(0, Math.min(35, commentSize));
  const normalizedHeadSize = Math.max(0, Math.min(20, headSize));
  const centerContentSize = Math.max(45, 100 - normalizedHeadSize - normalizedCommentSize);
  const splitMode = normalized.resizeEnabled ? 'resizable' : 'fixed';
  const splitLock = !normalized.resizeEnabled;
  const contentBackground = buildContentBackground(normalized);

  return {
    ...baseOption,
    mainKey: `zenchat.main-${normalized.uiLayout}-${normalized.layoutVersion}`,
    bodyKey: `zenchat.body-${normalized.uiLayout}-${normalized.layoutVersion}`,
    contentKey: `zenchat.content-${normalized.uiLayout}-${normalized.layoutVersion}`,
    leftSize: preset.leftSize,
    leftMin: preset.leftMin,
    leftMax: preset.leftMax,
    rightSize: preset.rightSize,
    rightMin: preset.rightMin,
    rightMax: preset.rightMax,
    mainSizes: clone(baseOption.mainSizes || preset.mainSizes),
    contentSizes: [
      { size: normalizedHeadSize },
      { size: centerContentSize },
      { size: normalizedCommentSize }
    ],
    main: {
      mode: splitMode,
      lock: splitLock,
      noGrip: !normalized.showResizeGrip
    },
    body: {
      mode: splitMode,
      lock: splitLock,
      noGrip: !normalized.showResizeGrip
    },
    content: {
      mode: splitMode,
      lock: splitLock,
      noGrip: !normalized.showResizeGrip
    },
    dividerColor: normalized.dividerColor,
    dividerHoverColor: normalized.dividerHoverColor,
    dividerSize: `${normalized.dividerSize}px`,
    splitterHitSize: `${normalized.splitterHitSize}px`,
    gapColor: normalized.gapColor,
    paneBorderColor: normalized.paneBorderEnabled ? normalized.paneBorderColor : 'transparent',
    paneBorderWidth: normalized.paneBorderEnabled ? `${normalized.paneBorderWidth}px` : '0px',
    contentBackground: contentBackground.background,
    contentBackgroundSize: contentBackground.size,
    contentBackgroundPosition: contentBackground.position,
    contentBackgroundRepeat: contentBackground.repeat,
    gripPosition: normalized.gripPosition,
    resizeCursor: normalized.resizeCursor,
    splitTheme: normalized.splitTheme,
    persistLayout: normalized.resizeEnabled && normalized.persistResizableLayout
  };
}

export function resetMessengerLayoutStorage() {
  if (typeof window === 'undefined') return 0;

  const keys = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith('layout__zenchat.')) {
      keys.push(key);
    }
  }

  keys.forEach((key) => window.localStorage.removeItem(key));
  window.dispatchEvent(new CustomEvent(MESSENGER_LAYOUT_CHANGED_EVENT, { detail: readSettings() }));
  return keys.length;
}

export function getDefaultMessengerLayoutSettings() {
  return clone(DEFAULT_SETTINGS);
}
